import React from "react"
import { Link } from "react-router-dom"
import {
	Flex,
	INNER_MAX_WIDTH,
	Text,
	ContactCard,
} from "@capioweb/ui-components"
import { IconUrl, XhtmlString } from "../../@types/CapioWeb/core"
import {
	BooleanProperty,
	LinkProperty,
	StringProperty,
} from "../../@types/EpiFoundationSpa/Property"
import { createMarkup, urlIsInternal } from "../../utils"
import { LocalesContext } from "../../context"
import { sendCtaClickLayerPush } from "../../utils/piwikPro"
import { Button } from "@capioweb/ui-components/src"

interface CommonHeroBlockProps {
	heading: StringProperty
	preamble: StringProperty | XhtmlString
	heroWhite?: BooleanProperty
	Cta?: LinkProperty
	linkList?: IconUrl[]
}

const Cta: React.FC<IconUrl> = (props) => (
	<ContactCard icon={props.icon} iconSize={4.5} variant="compact">
		<Flex maxw={22}>
			<Text variant="headline-3" as="span" truncate="twoLine" mb={0}>
				{props.linkText}
			</Text>
		</Flex>
	</ContactCard>
)

// Used on start page and unit listing
const CommonHeroBlock: React.FC<CommonHeroBlockProps> = (props) => {
	const [showMore, setShowMore] = React.useState(false)
	const [showLink, setShowLink] = React.useState(false)

	const contentRef = React.createRef<HTMLDivElement>()

	const { translate, translationKeys } = React.useContext(LocalesContext)

	const heroWhite = !!props.heroWhite?.value

	React.useLayoutEffect(() => {
		if (contentRef?.current) {
			if (contentRef.current.clientHeight < contentRef.current.scrollHeight) {
				setShowLink(true)
			} else {
				setShowMore(true)
			}
		}
	}, [contentRef])

	return (
		<Flex
			as="section"
			px={3}
			pt={{ _: 6.5, l: 10 }}
			pb={{ _: 4.5, l: 8.75 }}
			backgroundColor={heroWhite ? "none" : "blue33"}
		>
			<Flex
				alignItems={heroWhite ? "flex-start" : "center"}
				flexDirection="column"
				w={1}
				maxw={INNER_MAX_WIDTH}
				m="0 auto"
			>
				<Flex
					flexDirection="column"
					justifyContent="center"
					maxw={heroWhite ? 93 : 69}
					m={heroWhite ? "0" : "0 auto"}
				>
					{props.heading?.value && (
						<Text
							as="h1"
							variant="headline-1"
							textAlign={heroWhite ? "left" : "center"}
						>
							{props.heading.value}
						</Text>
					)}
					{props.preamble?.value && (
						<Flex
							ref={contentRef}
							w={1}
							justifyContent="center"
							h={showMore ? "auto" : { _: 12, m: 16 }}
							overflow="hidden"
						>
							<Text
								textAlign={heroWhite ? "left" : "center"}
								fontSize={{ _: 2, m: 2.5 }}
								lineHeight={{ _: 3, m: 4 }}
								mb={{ _: 2, m: 2.5 }}
							>
								<span
									dangerouslySetInnerHTML={createMarkup(props.preamble.value)}
								></span>
							</Text>
						</Flex>
					)}
				</Flex>
				{showLink && (
					<Text.Button
						fontSize={{ _: 2, m: 2.5 }}
						lineHeight={{ _: 3, m: 4 }}
						mb={0}
						onClick={() => setShowMore(!showMore)}
						style={{ textDecoration: "underline" }}
					>
						{showMore
							? translate(translationKeys.showLess)
							: translate(translationKeys.showMore)}
					</Text.Button>
				)}
				{props.linkList?.length > 0 && (
					<Flex
						flexDirection={{ _: "column", l: "row" }}
						w={{ _: 1, l: "calc(100% + 24px)" }}
						justifyContent="center"
						alignItems="center"
					>
						{props.linkList.map((link, i) => (
							<Flex
								flexBasis={{ _: "100%", l: "33.33%" }}
								px={{ l: 1.5 }}
								key={i}
								mb={{ _: 1, l: 0 }}
								w={1}
								maxw={{ _: 45, l: "none" }}
							>
								{!urlIsInternal(link.link) ? (
									<a
										href={link.link}
										style={{ width: "100%" }}
										onClick={() => sendCtaClickLayerPush(link.linkText)}
									>
										<Cta {...link} />
									</a>
								) : (
									<Link
										to={link?.link ?? "#"}
										style={{ width: "100%" }}
										onClick={() => sendCtaClickLayerPush(link.linkText)}
									>
										<Cta {...link} />
									</Link>
								)}
							</Flex>
						))}
					</Flex>
				)}
				{heroWhite && props.Cta && props.Cta?.href && (
					<Flex mx={{ _: "auto", m: "0" }}>
						<Button.Link
							to={props.Cta.href}
							target={props.Cta.target}
							title={props.Cta.title}
							variant="ghost"
							mt={3}
						>
							{props.Cta.text}
						</Button.Link>
					</Flex>
				)}
			</Flex>
		</Flex>
	)
}

export { CommonHeroBlock }
